import {
  WASH_ACTIVATION_SOURCE_LABELS,
  WASH_ACTIVATION_SOURCES,
  WASH_ACTIVATION_STATUS_LABELS,
  WASH_ACTIVATION_STATUSES,
  WASH_CYCLE_STATUS_LABELS,
  WASH_CYCLE_STATUSES,
  WASH_CYCLE_TRIGGER_LABELS,
  WASH_CYCLE_TRIGGERS,
  WASH_START_MODE_LABELS,
  type WashActivation,
  type WashCycle,
} from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  DateRangePicker,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClipboardPen, History, Power } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import {
  activationsQuery,
  type ActivationFilters,
  type CycleFilters,
  cyclesQuery,
  useWashActivations,
  useWashCycles,
  useWashUnits,
} from "../../../features/wash/queries.js";
import {
  ActivationStatusBadge,
  CycleStatusBadge,
  washSearchShape,
  WashStationPage,
  type WashStationContext,
} from "../../../features/wash/shared.js";
import { ManualCycleDialog } from "../../../features/wash/unit-dialogs.js";
import { formatDuration } from "../../../features/wash/wash-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { formatDate, formatDateTime } from "../../../lib/format.js";

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const uuid = z.string().uuid().optional().catch(undefined);

const searchSchema = z.object({
  ...washSearchShape,
  tab: z.enum(["cycles", "activations"]).optional().catch(undefined),
  unitId: uuid,
  trigger: z.enum(WASH_CYCLE_TRIGGERS).optional().catch(undefined),
  status: z.enum(WASH_CYCLE_STATUSES).optional().catch(undefined),
  from: localDateText,
  to: localDateText,
  aStatus: z.enum(WASH_ACTIVATION_STATUSES).optional().catch(undefined),
  source: z.enum(WASH_ACTIVATION_SOURCES).optional().catch(undefined),
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/yikama/calismalar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CyclesRoute,
});

/**
 * Yıkama çalışmaları (Prompt 18 web, ADR 0018 §8, §11): makinenin her çalışması (döngü) — ünite / tetik / durum / iş günü
 * süzgeçleri, imleçli sayfalama, Excel'e aktarma; MANUAL ünitede elle kayıt (`wash.operate`). "Başlatmalar" sekmesi
 * aktivasyonları listeler (kaynak, biçim, durum, kasa satışı bağı) — kod ve kod özeti YANITTA YOKTUR.
 */
function CyclesRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <WashStationPage title={t("menu.washCycles")} description={t("wash.cycles.description")}>
      {(context) => <CyclesBody context={context} />}
    </WashStationPage>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -6), to: today };
}

function CyclesBody({ context }: { context: WashStationContext }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const units = useWashUnits(context.station.id);
  const tab = search.tab ?? "cycles";
  const setSearch = (patch: Partial<Search>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });
  return (
    <Tabs
      value={tab}
      onValueChange={(next) => setSearch({ tab: next === "cycles" ? undefined : (next as Search["tab"]) })}
    >
      <TabsList aria-label={t("wash.cycles.tabsLabel")}>
        <TabsTrigger value="cycles">{t("wash.cycles.tabs.cycles")}</TabsTrigger>
        <TabsTrigger value="activations">{t("wash.cycles.tabs.activations")}</TabsTrigger>
      </TabsList>
      <TabsContent value="cycles" className="grid min-w-0 gap-4 [&>*]:min-w-0">
        <CyclesTab context={context} search={search} setSearch={setSearch} units={units.data ?? []} />
      </TabsContent>
      <TabsContent value="activations" className="grid min-w-0 gap-4 [&>*]:min-w-0">
        <ActivationsTab context={context} search={search} setSearch={setSearch} units={units.data ?? []} />
      </TabsContent>
    </Tabs>
  );
}

type UnitList = NonNullable<ReturnType<typeof useWashUnits>["data"]>;

function UnitFilter({
  units,
  value,
  onChange,
}: {
  units: UnitList;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <FormField label={t("wash.fields.unit")} className="w-52">
      {(control) => (
        <Select
          {...control}
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value || undefined)}
        >
          <option value="">{t("common.all")}</option>
          {units.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.name}
            </option>
          ))}
        </Select>
      )}
    </FormField>
  );
}

function CyclesTab({
  context,
  search,
  setSearch,
  units,
}: {
  context: WashStationContext;
  search: Search;
  setSearch: (patch: Partial<Search>) => void;
  units: UnitList;
}): ReactElement {
  const { t } = useTranslation();
  const [manual, setManual] = useState(false);
  const fallback = defaultRange();
  const filters: CycleFilters = {
    stationId: context.station.id,
    from: search.from ?? fallback.from,
    to: search.to ?? fallback.to,
    unitId: search.unitId,
    trigger: search.trigger,
    status: search.status,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useWashCycles(filters, pages.cursor);
  const manualUnits = units.filter(
    (unit) => unit.settings.startMode === "MANUAL" && unit.status === "ACTIVE",
  );

  const columns = useMemo<DataTableColumn<WashCycle>[]>(
    () => [
      {
        id: "endedAt",
        header: t("wash.fields.endedAt"),
        hideable: false,
        exportValue: (row) => row.endedAt,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.endedAt)}</span>,
      },
      {
        id: "unit",
        header: t("wash.fields.unit"),
        exportValue: (row) => row.unitName ?? row.unitRef ?? "",
        cell: (row) =>
          row.unitName ?? (
            <span className="text-muted-foreground">
              {row.unitRef ? `${row.unitRef} (${t("wash.cycles.unknownUnit")})` : "—"}
            </span>
          ),
      },
      {
        id: "program",
        header: t("wash.fields.program"),
        exportValue: (row) => `${row.programCode} ${row.programName ?? ""}`.trim(),
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="font-mono text-xs">{row.programCode}</span>
            {row.programName ? <span>{row.programName}</span> : null}
          </span>
        ),
      },
      {
        id: "trigger",
        header: t("wash.fields.trigger"),
        exportValue: (row) => WASH_CYCLE_TRIGGER_LABELS[row.trigger],
        cell: (row) => WASH_CYCLE_TRIGGER_LABELS[row.trigger],
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => WASH_CYCLE_STATUS_LABELS[row.status],
        cell: (row) => <CycleStatusBadge status={row.status} />,
      },
      {
        id: "duration",
        header: t("wash.fields.duration"),
        align: "right",
        exportValue: (row) => row.durationSeconds,
        cell: (row) => <span className="tabular">{formatDuration(row.durationSeconds)}</span>,
      },
      {
        id: "amount",
        header: t("wash.cycles.machineAmount"),
        align: "right",
        exportValue: (row) => (row.amount === null ? null : Number(row.amount) / 100),
        cell: (row) =>
          row.amount === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="grid justify-items-end gap-0.5">
              <MoneyText value={row.amount} />
              {row.paymentType ? (
                <span className="text-xs text-muted-foreground">
                  {t(`wash.paymentType.${row.paymentType}`)}
                </span>
              ) : null}
            </span>
          ),
      },
      {
        id: "localDay",
        header: t("wash.fields.localDay"),
        defaultVisible: false,
        exportValue: (row) => row.localDay,
        cell: (row) => formatDate(row.localDay),
      },
      {
        id: "links",
        header: t("wash.cycles.links"),
        cell: (row) => (
          <span className="grid gap-0.5 text-xs">
            {row.workOrderId ? (
              <Link
                to="/yikama/is-emirleri/$id"
                params={{ id: row.workOrderId }}
                className="underline underline-offset-4"
              >
                {t("wash.cycles.workOrder")}
              </Link>
            ) : null}
            {row.creditId ? (
              <Link
                to="/yikama/krediler/$id"
                params={{ id: row.creditId }}
                className="underline underline-offset-4"
              >
                {t("wash.cycles.credit")}
              </Link>
            ) : null}
            {row.dayClosingId ? <span>{t("wash.cycles.closed")}</span> : null}
            {row.warnings.length > 0 ? (
              <span className="text-warning">
                {t("wash.cycles.warnings", { codes: row.warnings.join(", ") })}
              </span>
            ) : null}
          </span>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <DateRangePicker
          label={t("wash.fields.localDay")}
          value={{ from: filters.from as LocalDate, to: filters.to as LocalDate }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
        <UnitFilter units={units} value={search.unitId} onChange={(unitId) => setSearch({ unitId })} />
        <FormField label={t("wash.fields.trigger")} className="w-52">
          {(control) => (
            <Select
              {...control}
              value={search.trigger ?? ""}
              onChange={(event) =>
                setSearch({ trigger: (event.target.value || undefined) as Search["trigger"] })
              }
            >
              <option value="">{t("common.all")}</option>
              {WASH_CYCLE_TRIGGERS.map((trigger) => (
                <option key={trigger} value={trigger}>
                  {WASH_CYCLE_TRIGGER_LABELS[trigger]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("common.status")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={search.status ?? ""}
              onChange={(event) =>
                setSearch({ status: (event.target.value || undefined) as Search["status"] })
              }
            >
              <option value="">{t("common.all")}</option>
              {WASH_CYCLE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {WASH_CYCLE_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        {context.permissions.canOperate && manualUnits.length > 0 ? (
          <Button variant="outline" className="ms-auto" onClick={() => setManual(true)}>
            <ClipboardPen aria-hidden="true" />
            {t("wash.manualCycle.open")}
          </Button>
        ) : null}
      </Card>
      <DataTable
        caption={t("wash.cycles.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<History />}
            title={t("wash.cycles.emptyTitle")}
            description={t("wash.cycles.emptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<WashCycle>(
            async (cursor) =>
              (
                await api.GET("/wash/cycles", {
                  params: { query: cyclesQuery(filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "yikama-calismalari",
            sheetName: t("menu.washCycles"),
            columns: visible,
            rows,
          });
        }}
      />
      {manual ? (
        <ManualCycleDialog
          open
          onOpenChange={setManual}
          units={manualUnits}
          defaultUnitId={search.unitId ?? null}
        />
      ) : null}
    </>
  );
}

function ActivationsTab({
  context,
  search,
  setSearch,
  units,
}: {
  context: WashStationContext;
  search: Search;
  setSearch: (patch: Partial<Search>) => void;
  units: UnitList;
}): ReactElement {
  const { t } = useTranslation();
  const filters: ActivationFilters = {
    stationId: context.station.id,
    unitId: search.unitId,
    status: search.aStatus,
    source: search.source,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useWashActivations(filters, pages.cursor);

  const columns = useMemo<DataTableColumn<WashActivation>[]>(
    () => [
      {
        id: "createdAt",
        header: t("wash.activations.createdAt"),
        hideable: false,
        exportValue: (row) => row.createdAt,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.createdAt)}</span>,
      },
      {
        id: "unit",
        header: t("wash.fields.unit"),
        exportValue: (row) => row.unitName ?? "",
        cell: (row) => row.unitName ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: "program",
        header: t("wash.fields.program"),
        exportValue: (row) => `${row.programCode} ${row.programName}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="font-mono text-xs">{row.programCode}</span>
            <span>{row.programName}</span>
          </span>
        ),
      },
      {
        id: "source",
        header: t("wash.activations.source"),
        exportValue: (row) => WASH_ACTIVATION_SOURCE_LABELS[row.source],
        cell: (row) => WASH_ACTIVATION_SOURCE_LABELS[row.source],
      },
      {
        id: "method",
        header: t("wash.activations.method"),
        exportValue: (row) => WASH_START_MODE_LABELS[row.method],
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{WASH_START_MODE_LABELS[row.method]}</span>
            {row.hasCode ? (
              <span className="text-xs text-muted-foreground">{t("wash.activations.hasCode")}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => WASH_ACTIVATION_STATUS_LABELS[row.status],
        cell: (row) => (
          <span className="grid gap-1">
            <ActivationStatusBadge status={row.status} />
            {row.failureReason ? (
              <span className="text-xs text-muted-foreground">{row.failureReason}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: "sale",
        header: t("wash.activations.sale"),
        exportValue: (row) => row.posAgentSaleId ?? row.posSaleId ?? "",
        cell: (row) =>
          row.posSaleId ? (
            <Link
              to="/kasa/satislar/$id"
              params={{ id: row.posSaleId }}
              className="underline underline-offset-4"
            >
              {t("wash.activations.openSale")}
              {row.posLineNo !== null ? ` · ${t("wash.activations.line", { no: row.posLineNo })}` : ""}
            </Link>
          ) : row.subscriptionId ? (
            <span className="text-xs">{t("wash.activations.subscription")}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "finishedAt",
        header: t("wash.activations.finishedAt"),
        defaultVisible: false,
        exportValue: (row) => row.finishedAt,
        cell: (row) => formatDateTime(row.finishedAt ?? row.startedAt),
      },
    ],
    [t],
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <UnitFilter units={units} value={search.unitId} onChange={(unitId) => setSearch({ unitId })} />
        <FormField label={t("common.status")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={search.aStatus ?? ""}
              onChange={(event) =>
                setSearch({ aStatus: (event.target.value || undefined) as Search["aStatus"] })
              }
            >
              <option value="">{t("common.all")}</option>
              {WASH_ACTIVATION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {WASH_ACTIVATION_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("wash.activations.source")} className="w-48">
          {(control) => (
            <Select
              {...control}
              value={search.source ?? ""}
              onChange={(event) =>
                setSearch({ source: (event.target.value || undefined) as Search["source"] })
              }
            >
              <option value="">{t("common.all")}</option>
              {WASH_ACTIVATION_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {WASH_ACTIVATION_SOURCE_LABELS[source]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </Card>
      <DataTable
        caption={t("wash.activations.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Power />}
            title={t("wash.activations.emptyTitle")}
            description={t("wash.activations.emptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<WashActivation>(
            async (cursor) =>
              (
                await api.GET("/wash/activations", {
                  params: { query: activationsQuery(filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "yikama-baslatmalari",
            sheetName: t("wash.cycles.tabs.activations"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}
